import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { Trash2, ShoppingBag, ArrowLeft, Minus, Plus, Package, Info } from 'lucide-react';

const Cart = () => {
  const { cart, loading, loadCart, updateItem, removeItem } = useCart();
  const navigate = useNavigate();
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const handleQuantity = async (itemId, newQty) => {
    if (newQty < 1) {
      await removeItem(itemId);
      return;
    }
    setUpdating(itemId);
    await updateItem(itemId, newQty);
    setUpdating(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full" />
      </div>
    );
  }

  const renderItem = (item) => {
    const isPack = !!item.sttora_pack_id;
    return (
      <motion.div key={item.id} layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -100, transition: { duration: 0.3 } }}
        className={`rounded-2xl p-4 shadow-sm border ${isPack
          ? 'bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800/50'
          : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700'}`}
      >
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden flex-shrink-0">
            {item.product_image ? (
              <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <Package className="w-8 h-8" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{item.product_name}</h3>
            {isPack ? (
              <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Sttora Pack</p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                ${item.price.toFixed(2)} c/u | VU: ${item.vu.toFixed(2)}
              </p>
            )}
          </div>
          {!isPack && (
            <>
              <div className="flex items-center gap-2">
                <motion.button whileTap={{ scale: 0.9 }}
                  onClick={() => handleQuantity(item.id, item.quantity - 1)}
                  disabled={updating === item.id}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors">
                  <Minus className="w-4 h-4" />
                </motion.button>
                <span className="w-8 text-center font-semibold">{item.quantity}</span>
                <motion.button whileTap={{ scale: 0.9 }}
                  onClick={() => handleQuantity(item.id, item.quantity + 1)}
                  disabled={updating === item.id}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors">
                  <Plus className="w-4 h-4" />
                </motion.button>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-900 dark:text-white">${item.total.toFixed(2)}</p>
                <p className="text-xs text-gray-500">VU: ${item.total_vu.toFixed(2)}</p>
              </div>
            </>
          )}
          <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
            onClick={() => removeItem(item.id)}
            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-all">
            <Trash2 className="w-4 h-4" />
          </motion.button>
        </div>
        {isPack && (
          <div className="mt-4 pt-3 border-t border-purple-200 dark:border-purple-800/50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-purple-700 dark:text-purple-300">${item.price.toFixed(2)}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">c/u | VU: ${item.vu.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2">
                <motion.button whileTap={{ scale: 0.9 }}
                  onClick={() => handleQuantity(item.id, item.quantity - 1)}
                  className="p-1.5 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors">
                  <Minus className="w-4 h-4 text-purple-600" />
                </motion.button>
                <span className="w-8 text-center font-semibold">{item.quantity}</span>
                <motion.button whileTap={{ scale: 0.9 }}
                  onClick={() => handleQuantity(item.id, item.quantity + 1)}
                  className="p-1.5 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors">
                  <Plus className="w-4 h-4 text-purple-600" />
                </motion.button>
                <span className="ml-3 font-bold text-purple-700 dark:text-purple-300">${item.total.toFixed(2)}</span>
              </div>
            </div>
            <details className="group mt-2">
              <summary className="flex items-center gap-1 text-xs text-purple-500 cursor-pointer hover:text-purple-700 select-none">
                <Info className="w-3 h-3" />
                Productos incluidos ({item.pack_items?.length || 0})
              </summary>
              <div className="mt-2 space-y-1 pl-1">
                {(item.pack_items || []).map((pi, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />
                    <span>{pi.product_name}</span>
                    <span className="text-xs text-gray-400 ml-auto">x{pi.quantity}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <motion.h1 initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
        className="text-3xl font-bold text-gray-900 dark:text-white mb-8 flex items-center gap-3">
        <ShoppingBag className="w-8 h-8" /> Carrito de Compras
      </motion.h1>

      {cart.items.length === 0 ? (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-20">
          <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 3 }}>
            <ShoppingBag className="w-20 h-20 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          </motion.div>
          <p className="text-xl text-gray-500 dark:text-gray-400 mb-4">Tu carrito está vacío</p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/store" className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-orange-500/25 transition-all">
              <ArrowLeft className="w-4 h-4" /> Ir a la Tienda
            </Link>
          </motion.div>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <AnimatePresence>
              {cart.items.map(item => renderItem(item))}
            </AnimatePresence>
          </div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 sticky top-24">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Resumen</h2>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium">${cart.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">VU Total</span>
                  <span className="font-medium">${cart.total_vu.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Artículos</span>
                  <span className="font-medium">{cart.item_count}</span>
                </div>
                <hr className="dark:border-gray-700" />
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-orange-600">${cart.subtotal.toFixed(2)}</span>
                </div>
              </div>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/store/checkout')}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-orange-500/30 transition-all">
                Proceder al Pago
              </motion.button>
              <Link to="/store" className="block text-center text-sm text-orange-600 mt-4 hover:text-orange-700 transition-colors">
                Seguir comprando
              </Link>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Cart;
